import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScrollText, ShieldCheck } from "lucide-react";

const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

/**
 * Planner-facing "Review & Sign" dialog for the booking agreement.
 *
 * This is the authoritative acceptance point: on submit it hits
 * POST /api/bookings/{id}/agreement/accept which atomically creates the
 * immutable snapshot, transitions the booking to `accepted`, and records the
 * signature. Legacy `PUT /bookings/{id}` accept path is still permitted for
 * admin fixes but should not be reached from this flow.
 */
export default function AcceptAgreementDialog({ booking, snapshot, policy, defaultName = "", onAccepted }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(defaultName);
  const [affirmed, setAffirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const deposit = Number(policy?.computed?.deposit_summary?.match(/\$([\d,]+\.\d{2})/)?.[1]?.replace(/,/g, "") || 0);

  const submit = async () => {
    if (!affirmed) return toast.error("Please affirm you agree to the terms");
    if ((name || "").trim().length < 2) return toast.error("Please type your full name to sign");
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/agreement/accept`, {
        signed_name: name.trim(),
        affirmed: true,
      });
      toast.success("Booking accepted and signed");
      setOpen(false);
      onAccepted?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not accept");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        data-testid="review-and-sign-btn"
        className="btn-sage rounded-full w-full h-11 mt-4"
        onClick={() => setOpen(true)}
      >
        <ShieldCheck className="w-4 h-4 mr-2" /> Review &amp; sign
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="accept-agreement-dialog" className="max-w-lg bg-[#FDFBF7] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Review &amp; sign booking agreement</DialogTitle>
            <DialogDescription className="text-xs text-[#5C5C5C]">
              This locks the agreement between you and the vendor. Any later vendor edits to prices or policies do not affect what's saved here.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2 text-sm">
            <dl className="grid grid-cols-2 gap-3 rounded-md bg-white border border-[#E8E2D9] p-4">
              <div><dt className="eyebrow">Vendor</dt><dd className="mt-1 font-serif text-base">{booking.vendor_name}</dd></div>
              <div><dt className="eyebrow">Event date</dt><dd className="mt-1">{new Date(booking.date).toLocaleDateString()} · {(booking.period || "").toUpperCase()}</dd></div>
              <div><dt className="eyebrow">Guests</dt><dd className="mt-1">{booking.guest_count}</dd></div>
              <div><dt className="eyebrow">Total</dt><dd className="mt-1 font-serif text-base">{fmt(booking.quote_amount)}</dd></div>
              <div><dt className="eyebrow">Deposit today</dt><dd className="mt-1">{policy?.computed?.deposit_summary}</dd></div>
              <div><dt className="eyebrow">Balance</dt><dd className="mt-1">{policy?.computed?.balance_summary}</dd></div>
            </dl>

            <div className="rounded-md bg-white border border-[#E8E2D9] p-4 space-y-2">
              <div className="eyebrow">Cancellation</div>
              <p className="leading-snug">{policy?.computed?.cancellation_summary}</p>
              {(snapshot?.rescheduling_policy || snapshot?.weather_policy || snapshot?.late_payment_policy || snapshot?.damage_policy || snapshot?.other_terms) && (
                <div className="pt-2 text-[12px] text-[#5C5C5C] space-y-1">
                  {snapshot.rescheduling_policy && <div><strong className="text-[color:var(--text)]">Rescheduling:</strong> {snapshot.rescheduling_policy}</div>}
                  {snapshot.weather_policy && <div><strong className="text-[color:var(--text)]">Weather:</strong> {snapshot.weather_policy}</div>}
                  {snapshot.late_payment_policy && <div><strong className="text-[color:var(--text)]">Late payment:</strong> {snapshot.late_payment_policy}</div>}
                  {snapshot.damage_policy && <div><strong className="text-[color:var(--text)]">Damages:</strong> {snapshot.damage_policy}</div>}
                  {snapshot.other_terms && <div><strong className="text-[color:var(--text)]">Other:</strong> {snapshot.other_terms}</div>}
                </div>
              )}
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="agreement-affirm-checkbox"
                checked={affirmed}
                onChange={(e) => setAffirmed(e.target.checked)}
                className="mt-1 shrink-0"
              />
              <span className="text-[13px] leading-snug">
                I have reviewed and agree to the booking details, payment terms, cancellation policy, and vendor terms shown above.
              </span>
            </label>

            <div>
              <label className="eyebrow mb-1 block">Sign by typing your full name</label>
              <Input
                data-testid="agreement-signed-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Jane Doe"
                maxLength={120}
                className="bg-white h-11"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} className="rounded-full">Cancel</Button>
            <Button
              onClick={submit}
              disabled={busy}
              className="btn-sage rounded-full"
              data-testid="agreement-submit-btn"
            >
              {busy ? "Signing…" : "I agree — sign & accept"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * Read-only Agreement panel — visible to planner, vendor owner, admin once the
 * booking has moved past `quoted`. Fetches from GET /bookings/{id}/agreement.
 */
export function AgreementPanel({ bookingId }) {
  const [agr, setAgr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/bookings/${bookingId}/agreement`)
      .then(({ data }) => setAgr(data))
      .catch(() => setAgr(null))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [bookingId]);

  return (
    <details className="card-elegant p-5" data-testid="agreement-panel">
      <summary className="cursor-pointer inline-flex items-center gap-2 text-sm" onClick={() => { if (!agr) load(); }}>
        <ScrollText className="w-4 h-4 text-[color:var(--text-muted)]" />
        <span className="eyebrow">Booking agreement</span>
      </summary>
      <div className="mt-4 text-sm">
        {loading && <div className="text-[color:var(--text-muted)]">Loading…</div>}
        {!loading && agr && agr.available === false && (
          <div className="text-[color:var(--text-muted)]" data-testid="agreement-legacy-notice">
            {agr.reason || "Booking agreement snapshot unavailable."}
          </div>
        )}
        {!loading && agr && agr.available !== false && agr.agreement_id && (
          <div className="space-y-3" data-testid="agreement-content">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--text-muted)]">
              Accepted: {new Date(agr.signed_at).toLocaleString()} · Signed by <span className="text-[color:var(--text)] font-medium">{agr.signed_name}</span>
            </div>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 rounded-md bg-white border border-[#E8E2D9] p-4">
              <div><dt className="eyebrow">Total</dt><dd className="mt-1 font-serif text-base">{fmt(agr.quote_amount)}</dd></div>
              <div><dt className="eyebrow">Deposit</dt><dd className="mt-1">{fmt(agr.deposit_dollars)}</dd></div>
              <div><dt className="eyebrow">Balance</dt><dd className="mt-1">{fmt(agr.balance_dollars)}</dd></div>
              <div><dt className="eyebrow">Guests</dt><dd className="mt-1">{agr.guest_count}</dd></div>
              <div className="col-span-2"><dt className="eyebrow">Event</dt><dd className="mt-1">{new Date(agr.event_date).toLocaleDateString()} · {(agr.event_period || "").toUpperCase()}</dd></div>
            </dl>
            <div className="rounded-md bg-white border border-[#E8E2D9] p-4">
              <div className="eyebrow mb-1">Cancellation policy at acceptance</div>
              <p className="text-[13px] leading-snug">{agr.cancellation_summary}</p>
            </div>
            <div className="text-[10px] text-[color:var(--text-muted)]">
              Agreement ID: {agr.agreement_id} · v{agr.agreement_version}
            </div>
          </div>
        )}
      </div>
    </details>
  );
}
